import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  Paper,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import {
  SteerageStatsData,
  MonthlyProviderTypeData,
  getSteerageStats,
} from '../../services/steerageService';
import { Severity } from '../../types';
import useSnackbar from '../../hooks/useSnackbar';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { ClaimsLocationMap } from '../../components/ClaimsLocationMap';
import { useAuthContext } from 'contexts/authContext';
import TenantSelector from '../../components/TenantSelector';

const useStyles = makeStyles((theme) => ({
  statsContainer: {
    marginBottom: theme.spacing(4),
  },
  statCard: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1a1f36',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
  },
  totalNumber: {
    fontSize: 80,
    fontWeight: 500,
    textAlign: 'center',
    margin: theme.spacing(2, 0),
  },
  title: {
    fontSize: 16,
    fontWeight: 500,
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.8)',
  },
  barContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
    height: '100%',
    width: '100%',
    padding: theme.spacing(2, 0),
  },
  bar: {
    width: '40px',
    borderRadius: '4px 4px 0 0',
    position: 'relative',
    transition: 'height 0.5s ease',
    '&:hover': {
      opacity: 0.8,
    },
  },
  barLabel: {
    textAlign: 'center',
    marginTop: theme.spacing(1),
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.7)',
  },
  barValue: {
    position: 'absolute',
    top: -25,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 'bold',
    color: 'white',
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: theme.spacing(2),
    color: 'white',
  },
  chartCard: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#1a1f36',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: 200,
  },
}));

interface BarChartProps {
  title: string;
  data: Array<{ name: string; value: number; color: string }>;
  loading: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomMonthlyTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        style={{
          padding: '10px',
          border: '1px solid #ccc',
          backgroundColor: '#1a1f36',
          color: 'white',
        }}
      >
        <Typography variant="body2" style={{ marginBottom: '4px' }}>
          {label}
        </Typography>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {payload.map((entry: any, index: number) => (
          <Typography
            key={index}
            variant="body2"
            style={{ color: entry.color }}
          >
            {`${entry.dataKey === 'er' ? 'ER' : 'Urgent Care'}: ${entry.value}`}
          </Typography>
        ))}
      </Paper>
    );
  }
  return null;
};

interface MonthlyBarChartProps {
  title: string;
  data: MonthlyProviderTypeData[];
  loading: boolean;
}

const MonthlyProviderTypesChart: React.FC<MonthlyBarChartProps> = ({
  title,
  data,
  loading,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  if (loading) {
    return (
      <Paper className={classes.chartCard}>
        <Typography className={classes.chartTitle}>{title}</Typography>
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} color="primary" />
        </div>
      </Paper>
    );
  }

  // Format month labels for display
  const formattedData = data.map((item) => {
    const [year, month] = item.month.split('-');
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return {
      ...item,
      monthLabel: `${monthNames[parseInt(month) - 1]} ${year}`,
    };
  });

  // Calculate Y-axis max value
  const maxValue = Math.max(
    ...formattedData.map((item) => Math.max(item.er, item.urgentCare)),
    1
  );
  const yAxisMax = maxValue + Math.ceil(maxValue * 0.1); // Add 10% padding

  return (
    <Paper className={classes.chartCard}>
      <Typography className={classes.chartTitle}>{title}</Typography>
      <Box style={{ width: '100%', height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={formattedData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#333a56" />
            <XAxis
              dataKey="monthLabel"
              stroke="#fff"
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis stroke="#fff" allowDecimals={false} domain={[0, yAxisMax]} />
            <Tooltip content={<CustomMonthlyTooltip />} />
            <Legend />
            <Bar dataKey="er" name={t('ER')} fill="#e53935" barSize={30}>
              <LabelList dataKey="er" position="top" />
            </Bar>
            <Bar
              dataKey="urgentCare"
              name={t('Urgent Care')}
              fill="#43a047"
              barSize={30}
            >
              <LabelList dataKey="urgentCare" position="top" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export const SimpleBarChart: React.FC<BarChartProps> = ({
  title,
  data,
  loading,
}) => {
  const classes = useStyles();

  if (loading) {
    return (
      <Paper className={classes.chartCard}>
        <Typography className={classes.chartTitle}>{title}</Typography>
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} color="primary" />
        </div>
      </Paper>
    );
  }

  // Find the maximum value to calculate percentages
  const maxValue = Math.max(...data.map((item) => item.value), 1);

  // Calculate Y-axis max value (max data value + 20)
  const yAxisMax = maxValue + 20;

  // Calculate evenly spaced ticks (4 ticks: 0, 1/3 of max, 2/3 of max, max)
  const yAxisTicks = [
    0,
    Math.round(yAxisMax / 3),
    Math.round((2 * yAxisMax) / 3),
    yAxisMax,
  ];

  return (
    <Paper className={classes.chartCard}>
      <Typography className={classes.chartTitle}>{title}</Typography>
      <Box style={{ width: '100%', height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333a56" />
            <XAxis dataKey="name" stroke="#fff" />
            <YAxis
              stroke="#fff"
              allowDecimals={false}
              domain={[0, yAxisMax]}
              ticks={yAxisTicks}
            />
            <Bar dataKey="value" fill={'#8884d8'} barSize={30}>
              <LabelList dataKey="value" position="top" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export const SteerageStats: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [statsData, setStatsData] = useState<SteerageStatsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const { user } = useAuthContext();
  const [currentTenant, setCurrentTenant] = useState<number>(0);

  const show = useSnackbar();

  const fetchStatsData = useCallback(async () => {
    try {
      setLoading(true);
      const stats = await getSteerageStats(currentTenant || undefined);
      setStatsData(stats);
    } catch (error) {
      show('Failed to load steerage statistics', Severity.error);
    } finally {
      setLoading(false);
    }
  }, [show, currentTenant]);

  useEffect(() => {
    fetchStatsData();
  }, [currentTenant]);

  // Prepare stats data for charts
  const getSuccessFailureChartData = () => {
    if (!statsData) return [];
    return [
      { name: 'Success', value: statsData.success, color: '#4caf50' },
      { name: 'Failed', value: statsData.failed, color: '#f44336' },
    ];
  };

  const getProviderTypeChartData = () => {
    if (!statsData) return [];
    const { providerTypes } = statsData;
    return [
      { name: 'ER', value: providerTypes.er, color: '#e53935' },
      {
        name: 'Urgent Care',
        value: providerTypes.urgentCare,
        color: '#43a047',
      },
      { name: 'Hospital', value: providerTypes.hospital, color: '#fb8c00' },
      { name: 'Dental', value: providerTypes.dental, color: '#1e88e5' },
      { name: 'Other', value: providerTypes.other, color: '#757575' },
    ];
  };

  const getMonthlyProviderTypeChartData = (): MonthlyProviderTypeData[] => {
    if (!statsData || !statsData.monthlyProviderTypes) return [];
    return statsData.monthlyProviderTypes;
  };

  // Check user role for displaying tenant selector
  const { role } = user || { role: '' };

  return (
    <Box className={classes.statsContainer}>
      <Grid container spacing={3}>
        {role === 'super_user' && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            style={{ marginBottom: 10 }}
          >
            <Box mb={2}>
              <TenantSelector
                value={currentTenant}
                onChange={(val) => setCurrentTenant(val)}
                // includeAllOption={true} // Default is true
              />
            </Box>
          </Box>
        )}
        <Grid item xs={12}>
          <Paper className={classes.statCard}>
            <Typography className={classes.title}>
              {t('Number of total steerages')}
            </Typography>
            <Typography className={classes.totalNumber}>
              {loading ? '...' : statsData?.total || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SimpleBarChart
                title={t('Success vs Failed')}
                data={getSuccessFailureChartData()}
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SimpleBarChart
                title={t('Provider Types')}
                data={getProviderTypeChartData()}
                loading={loading}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <MonthlyProviderTypesChart
            title={t('Monthly Provider Types - ER vs Urgent Care')}
            data={getMonthlyProviderTypeChartData()}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12}>
          <ClaimsLocationMap
            providerLocations={statsData?.providerLocations || []}
            isLoading={loading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
